import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2, Search, Target } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const AdminJobsPage = () => {
  const { t } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    company: 'WeDo HR Services',
    department: '',
    location: '',
    location_ar: '',
    country: '',
    country_ar: '',
    region: '',
    employment_type: 'full-time',
    work_model: 'onsite',
    salary_min: '',
    salary_max: '',
    currency: 'EUR',
    description: '',
    description_ar: '',
    requirements: '',
    requirements_ar: '',
    responsibilities: '',
    responsibilities_ar: '',
    benefits: '',
    benefits_ar: '',
    skills: '',
    skills_ar: '',
    experience_years: '',
    is_featured: false,
    status: 'active',
    // ATS Screening Requirements
    required_experience_years: '',
    required_skills: '',
    preferred_skills: '',
    preferred_skills_ar: '',
    required_languages: '',
    preferred_domains: '',
    education_level: '',
    screening_criteria: '',
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/admin/jobs`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        title: job.title || '',
        title_ar: job.title_ar || '',
        company: job.company || 'WeDo HR Services',
        department: job.department || '',
        location: job.location || '',
        location_ar: job.location_ar || '',
        country: job.country || '',
        country_ar: job.country_ar || '',
        region: job.region || '',
        employment_type: job.employment_type || 'full-time',
        work_model: job.work_model || 'onsite',
        salary_min: job.salary_min?.toString() || '',
        salary_max: job.salary_max?.toString() || '',
        currency: job.currency || 'EUR',
        description: job.description || '',
        description_ar: job.description_ar || '',
        requirements: job.requirements?.join('\n') || '',
        requirements_ar: job.requirements_ar?.join('\n') || '',
        responsibilities: job.responsibilities?.join('\n') || '',
        responsibilities_ar: job.responsibilities_ar?.join('\n') || '',
        benefits: job.benefits?.join('\n') || '',
        benefits_ar: job.benefits_ar?.join('\n') || '',
        skills: job.skills?.join(', ') || '',
        skills_ar: job.skills_ar?.join(', ') || '',
        experience_years: job.experience_years?.toString() || '',
        is_featured: job.is_featured || false,
        status: job.status || 'active',
        // ATS Screening Requirements
        required_experience_years: job.required_experience_years?.toString() || '',
        required_skills: job.required_skills?.join(', ') || '',
        preferred_skills: job.preferred_skills?.join(', ') || '',
        preferred_skills_ar: job.preferred_skills_ar?.join(', ') || '',
        required_languages: job.required_languages?.join(', ') || '',
        preferred_domains: job.preferred_domains?.join(', ') || '',
        education_level: job.education_level || '',
        screening_criteria: job.screening_criteria || '',
      });
    } else {
      setEditingJob(null);
      setFormData({
        title: '',
        title_ar: '',
        company: 'WeDo HR Services',
        department: '',
        location: '',
        location_ar: '',
        country: '',
        country_ar: '',
        region: '',
        employment_type: 'full-time',
        work_model: 'onsite',
        salary_min: '',
        salary_max: '',
        currency: 'EUR',
        description: '',
        description_ar: '',
        requirements: '',
        requirements_ar: '',
        responsibilities: '',
        responsibilities_ar: '',
        benefits: '',
        benefits_ar: '',
        skills: '',
        skills_ar: '',
        experience_years: '',
        is_featured: false,
        status: 'active',
        // ATS Screening Requirements
        required_experience_years: '',
        required_skills: '',
        preferred_skills: '',
        preferred_skills_ar: '',
        required_languages: '',
        preferred_domains: '',
        education_level: '',
        screening_criteria: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
      salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
      experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
      requirements: formData.requirements.split('\n').filter(Boolean),
      requirements_ar: formData.requirements_ar.split('\n').filter(Boolean),
      responsibilities: formData.responsibilities.split('\n').filter(Boolean),
      responsibilities_ar: formData.responsibilities_ar.split('\n').filter(Boolean),
      benefits: formData.benefits.split('\n').filter(Boolean),
      benefits_ar: formData.benefits_ar.split('\n').filter(Boolean),
      skills: formData.skills.split(',').map(s => s.trim()).filter(Boolean),
      skills_ar: formData.skills_ar.split(',').map(s => s.trim()).filter(Boolean),
      // ATS Screening Requirements
      required_experience_years: formData.required_experience_years ? parseInt(formData.required_experience_years) : null,
      required_skills: formData.required_skills.split(',').map(s => s.trim()).filter(Boolean),
      preferred_skills: formData.preferred_skills.split(',').map(s => s.trim()).filter(Boolean),
      preferred_skills_ar: formData.preferred_skills_ar.split(',').map(s => s.trim()).filter(Boolean),
      required_languages: formData.required_languages.split(',').map(s => s.trim()).filter(Boolean),
      preferred_domains: formData.preferred_domains.split(',').map(s => s.trim()).filter(Boolean),
      education_level: formData.education_level || null,
      screening_criteria: formData.screening_criteria || null,
    };

    try {
      const url = editingJob 
        ? `${API}/admin/jobs/${editingJob.job_id}`
        : `${API}/admin/jobs`;
      
      const response = await fetch(url, {
        method: editingJob ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingJob ? 'Job updated successfully' : 'Job created successfully');
        setDialogOpen(false);
        fetchJobs();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to save job');
      }
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;

    try {
      const response = await fetch(`${API}/admin/jobs/${jobId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Job deleted successfully');
        fetchJobs();
      } else {
        toast.error('Failed to delete job');
      }
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(search.toLowerCase()) ||
    job.company.toLowerCase().includes(search.toLowerCase()) ||
    job.location.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-red-100 text-red-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6" data-testid="admin-jobs-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">{t('admin.jobs')}</h1>
          <p className="text-[#6B7280]">Manage job listings</p>
        </div>
        <Button className="btn-primary" onClick={() => openDialog()} data-testid="add-job-btn">
          <Plus className="h-4 w-4 me-2" />
          {t('admin.addNew')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder={t('admin.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-9"
                data-testid="search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-8 text-[#6B7280]">{t('admin.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>{t('admin.status')}</TableHead>
                    <TableHead className="text-end">{t('admin.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJobs.map((job, index) => (
                    <TableRow key={job.job_id} data-testid={`job-row-${index}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#111827]">{job.title}</p>
                          <p className="text-sm text-[#6B7280]">{job.company}</p>
                        </div>
                      </TableCell>
                      <TableCell>{job.location}, {job.country}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{job.employment_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => openDialog(job)} data-testid={`edit-job-${index}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(job.job_id)} data-testid={`delete-job-${index}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingJob ? t('admin.edit') : t('admin.addNew')} Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Job Title (English) *</Label>
                <Input id="title" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="title_ar">Job Title (Arabic)</Label>
                <Input id="title_ar" value={formData.title_ar} onChange={(e) => handleChange('title_ar', e.target.value)} dir="rtl" placeholder="المسمى الوظيفي" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input id="company" value={formData.company} onChange={(e) => handleChange('company', e.target.value)} />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location (English) *</Label>
                <Input id="location" value={formData.location} onChange={(e) => handleChange('location', e.target.value)} required placeholder="e.g., Berlin" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location_ar">Location (Arabic)</Label>
                <Input id="location_ar" value={formData.location_ar} onChange={(e) => handleChange('location_ar', e.target.value)} dir="rtl" placeholder="مثال: برلين" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country (English) *</Label>
                <Input id="country" value={formData.country} onChange={(e) => handleChange('country', e.target.value)} required placeholder="e.g., Germany" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country_ar">Country (Arabic)</Label>
                <Input id="country_ar" value={formData.country_ar} onChange={(e) => handleChange('country_ar', e.target.value)} dir="rtl" placeholder="مثال: ألمانيا" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Region</Label>
                <Select value={formData.region} onValueChange={(v) => handleChange('region', v)} data-testid="region-select">
                  <SelectTrigger><SelectValue placeholder="Select region..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Europe">Europe</SelectItem>
                    <SelectItem value="Middle East">Middle East</SelectItem>
                    <SelectItem value="North Africa">North Africa</SelectItem>
                    <SelectItem value="Sub-Saharan Africa">Sub-Saharan Africa</SelectItem>
                    <SelectItem value="Asia Pacific">Asia Pacific</SelectItem>
                    <SelectItem value="North America">North America</SelectItem>
                    <SelectItem value="Latin America">Latin America</SelectItem>
                    <SelectItem value="GCC">GCC</SelectItem>
                    <SelectItem value="DACH">DACH (Germany/Austria/Switzerland)</SelectItem>
                    <SelectItem value="Levant">Levant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input id="department" value={formData.department} onChange={(e) => handleChange('department', e.target.value)} placeholder="e.g., Engineering, HR" />
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select value={formData.employment_type} onValueChange={(v) => handleChange('employment_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                    <SelectItem value="internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Work Model</Label>
                <Select value={formData.work_model} onValueChange={(v) => handleChange('work_model', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onsite">On-site</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="remote">Remote</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salary_min">Salary Min</Label>
                <Input id="salary_min" type="number" value={formData.salary_min} onChange={(e) => handleChange('salary_min', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="salary_max">Salary Max</Label>
                <Input id="salary_max" type="number" value={formData.salary_max} onChange={(e) => handleChange('salary_max', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => handleChange('currency', v)} data-testid="currency-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="SYP">SYP (Syrian Pound)</SelectItem>
                    <SelectItem value="SAR">SAR (Saudi Riyal)</SelectItem>
                    <SelectItem value="EGP">EGP (Egyptian Pound)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (English) *</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} rows={4} required placeholder="Detailed job description in English..." />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description_ar">Description (Arabic)</Label>
              <Textarea id="description_ar" value={formData.description_ar} onChange={(e) => handleChange('description_ar', e.target.value)} rows={4} dir="rtl" placeholder="وصف الوظيفة بالعربية..." />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="responsibilities">Responsibilities (English, one per line)</Label>
                <Textarea id="responsibilities" value={formData.responsibilities} onChange={(e) => handleChange('responsibilities', e.target.value)} rows={3} placeholder="Lead project planning&#10;Coordinate with teams&#10;Deliver on deadlines" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="responsibilities_ar">Responsibilities (Arabic)</Label>
                <Textarea id="responsibilities_ar" value={formData.responsibilities_ar} onChange={(e) => handleChange('responsibilities_ar', e.target.value)} rows={3} dir="rtl" placeholder="قيادة تخطيط المشاريع&#10;التنسيق مع الفرق&#10;الالتزام بالمواعيد" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="requirements">Requirements (English, one per line)</Label>
                <Textarea id="requirements" value={formData.requirements} onChange={(e) => handleChange('requirements', e.target.value)} rows={3} placeholder="5+ years experience&#10;Bachelor's degree&#10;Strong communication skills" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="requirements_ar">Requirements (Arabic)</Label>
                <Textarea id="requirements_ar" value={formData.requirements_ar} onChange={(e) => handleChange('requirements_ar', e.target.value)} rows={3} dir="rtl" placeholder="خبرة 5 سنوات أو أكثر&#10;درجة البكالوريوس&#10;مهارات تواصل قوية" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="benefits">Benefits (English, one per line)</Label>
                <Textarea id="benefits" value={formData.benefits} onChange={(e) => handleChange('benefits', e.target.value)} rows={3} placeholder="Competitive salary&#10;Health insurance&#10;Annual bonus" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="benefits_ar">Benefits (Arabic)</Label>
                <Textarea id="benefits_ar" value={formData.benefits_ar} onChange={(e) => handleChange('benefits_ar', e.target.value)} rows={3} dir="rtl" placeholder="راتب تنافسي&#10;تأمين صحي&#10;مكافأة سنوية" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="skills">Skills (English, comma separated)</Label>
                <Input id="skills" value={formData.skills} onChange={(e) => handleChange('skills', e.target.value)} placeholder="JavaScript, React, Node.js" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="skills_ar">Skills (Arabic, comma separated)</Label>
                <Input id="skills_ar" value={formData.skills_ar} onChange={(e) => handleChange('skills_ar', e.target.value)} dir="rtl" placeholder="جافاسكريبت، رياكت، نود.جي.إس" />
              </div>
            </div>

            {/* ATS Screening Requirements Section */}
            <div className="pt-4 mt-4 border-t border-dashed border-[#E5E7EB]">
              <div className="flex items-center gap-2 mb-4">
                <Target className="h-5 w-5 text-[#33AADD]" />
                <h3 className="font-semibold text-[#111827]">AI Screening Requirements</h3>
                <span className="text-xs text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded">For candidate matching</span>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="required_experience_years">Minimum Years of Experience</Label>
                  <Input 
                    id="required_experience_years" 
                    type="number" 
                    min="0"
                    value={formData.required_experience_years} 
                    onChange={(e) => handleChange('required_experience_years', e.target.value)} 
                    placeholder="e.g., 5"
                    data-testid="required-experience-years"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Minimum Education Level</Label>
                  <Select value={formData.education_level} onValueChange={(v) => handleChange('education_level', v)}>
                    <SelectTrigger data-testid="education-level-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high_school">High School</SelectItem>
                      <SelectItem value="associate">Associate Degree</SelectItem>
                      <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                      <SelectItem value="master">Master's Degree</SelectItem>
                      <SelectItem value="phd">PhD / Doctorate</SelectItem>
                      <SelectItem value="any">Any</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2 mt-4">
                <Label htmlFor="required_skills">Required Skills (must-have, comma separated)</Label>
                <Input 
                  id="required_skills" 
                  value={formData.required_skills} 
                  onChange={(e) => handleChange('required_skills', e.target.value)} 
                  placeholder="Python, Data Analysis, SQL"
                  data-testid="required-skills-input"
                />
                <p className="text-xs text-[#6B7280]">Critical skills candidates must have</p>
              </div>

              <div className="space-y-2 mt-4">
                <Label htmlFor="preferred_skills">Preferred Skills (nice-to-have, comma separated)</Label>
                <Input 
                  id="preferred_skills" 
                  value={formData.preferred_skills} 
                  onChange={(e) => handleChange('preferred_skills', e.target.value)} 
                  placeholder="Machine Learning, Tableau, Cloud (AWS/GCP)"
                  data-testid="preferred-skills-input"
                />
                <p className="text-xs text-[#6B7280]">Skills that would be a plus</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="required_languages">Required Languages (comma separated)</Label>
                  <Input 
                    id="required_languages" 
                    value={formData.required_languages} 
                    onChange={(e) => handleChange('required_languages', e.target.value)} 
                    placeholder="English, Arabic"
                    data-testid="required-languages-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_domains">Preferred Industry Domains (comma separated)</Label>
                  <Input 
                    id="preferred_domains" 
                    value={formData.preferred_domains} 
                    onChange={(e) => handleChange('preferred_domains', e.target.value)} 
                    placeholder="Oil & Gas, Banking, Telecom"
                    data-testid="preferred-domains-input"
                  />
                </div>
              </div>

              <div className="space-y-2 mt-4">
                <Label htmlFor="screening_criteria">AI Screening Criteria (free text)</Label>
                <Textarea 
                  id="screening_criteria" 
                  value={formData.screening_criteria} 
                  onChange={(e) => handleChange('screening_criteria', e.target.value)} 
                  rows={3}
                  placeholder="Describe the ideal candidate profile for AI matching. E.g., 'Looking for someone with strong analytical skills and experience in large-scale data projects. Previous Oil & Gas industry exposure is a significant advantage.'"
                  data-testid="screening-criteria-textarea"
                />
                <p className="text-xs text-[#6B7280]">This helps the AI understand what you're looking for beyond just skills</p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('admin.cancel')}</Button>
              <Button type="submit" className="btn-primary">{t('admin.save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminJobsPage;
